import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { getSystemTypeDescriptionByValue } from 'src/app/domain/registration/enums/system-type.enum';
import { AuthService } from 'src/app/views/auth/shared/auth.service';

@Component({
  selector: 'app-plan-summary-dialog',
  templateUrl: './plan-summary-dialog.component.html',
  styleUrls: ['./plan-summary-dialog.component.sass'],
})
export class PlanSummaryDialogComponent {
  constructor(
    public dialogRef: MatDialogRef<PlanSummaryDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private authService: AuthService
  ) {}

  get plan(): any {
    return this.data?.plan || {};
  }

  get selectedPlan(): any {
    return this.plan || {};
  }

  get selectedSystem(): any {
    return this.plan?.system || null;
  }

  get systemId(): string {
    return String(this.plan?.system?.id || this.plan?.systemId || '--');
  }

  get systemName(): string {
    return this.plan?.system?.name || this.plan?.system || '--';
  }

  get systemType(): any {
    return this.plan?.system?.type ?? this.plan?.type ?? '--';
  }

  get selectedSolutions(): string[] {
    const fromVariations = Array.isArray(this.plan?.variations)
      ? this.plan.variations
          .map((variation: any) => String(variation?.solucao || variation?.systemName || '').trim())
          .filter((name: string) => !!name)
      : [];

    if (fromVariations.length > 0) {
      return Array.from(new Set(fromVariations));
    }

    const fallback = String(this.systemName || '').trim();
    return fallback && fallback !== '--' ? [fallback] : [];
  }

  get modules(): any[] {
    if (Array.isArray(this.plan?.modules) && this.plan.modules.length > 0) {
      return this.plan.modules;
    }

    const fromVariations = Array.isArray(this.plan?.variations)
      ? this.plan.variations.flatMap((variation: any) =>
          Array.isArray(variation?.modules) ? variation.modules : []
        )
      : [];

    const seen = new Set<string>();
    return fromVariations.filter((module: any) => {
      const key = String(module?.id || module?.name || '').trim();
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  get selectedPartners(): Array<{ id: string; name: string }> {
    const fromPartnersList = Array.isArray(this.plan?.partnersList) ? this.plan.partnersList : [];
    const fromPartners = Array.isArray(this.plan?.partners) ? this.plan.partners : [];
    const source = [...fromPartnersList, ...fromPartners];
    const unique = new Map<string, { id: string; name: string }>();

    source.forEach((item: any) => {
      const id = String(item?.id || item?.partnerId || item?.partner?.id || '').trim();
      const name = String(item?.name || item?.partner?.name || '').trim();
      const key = id || name.toLowerCase();
      if (!key) return;
      if (!unique.has(key)) {
        unique.set(key, { id: id || key, name: name || id || '--' });
      }
    });

    return Array.from(unique.values());
  }

  get variationRows(): any[] {
    if (Array.isArray(this.plan?.variations) && this.plan.variations.length > 0) {
      return this.plan.variations;
    }

    return [{
      codigo: '--',
      nome: this.selectedPlan?.name || '--',
      tipoSolucao: this.getSystemTypeDescription(this.selectedPlan?.type),
      solucao: this.systemName,
      valorMensalidade: this.plan?.monthlyValue ?? this.plan?.valuePerAccess ?? 0,
      custo: this.plan?.cost ?? 0,
      status: this.selectedPlan?.active === false ? 'INATIVO' : 'ATIVO',
    }];
  }

  get totalPlanValue(): number {
    if (Array.isArray(this.plan?.variations) && this.plan.variations.length > 0) {
      return this.plan.variations.reduce((sum: number, variation: any) => {
        const value = Number(variation?.valorMensalidade ?? variation?.valuePerAccess ?? 0);
        return sum + (Number.isFinite(value) ? value : 0);
      }, 0);
    }

    const monthly = Number(this.plan?.monthlyValue ?? this.plan?.valuePerAccess ?? 0);
    return Number.isFinite(monthly) ? monthly : 0;
  }

  get totalCost(): number {
    if (Array.isArray(this.plan?.variations) && this.plan.variations.length > 0) {
      return this.plan.variations.reduce((sum: number, variation: any) => {
        const value = Number(variation?.custo ?? variation?.cost ?? 0);
        return sum + (Number.isFinite(value) ? value : 0);
      }, 0);
    }

    const cost = Number(this.plan?.cost ?? 0);
    return Number.isFinite(cost) ? cost : 0;
  }

  getTotalValue(): number {
    return this.isPartnerUser ? this.totalPlanValue : this.totalPlanValue + this.totalCost;
  }

  get isPartnerUser(): boolean {
    return this.authService.isPartner();
  }

  getSystemTypeDescription(type: any): string {
    return getSystemTypeDescriptionByValue(type) || String(type || '--');
  }

  close(): void {
    this.dialogRef.close();
  }
}
